import { inject, injectable } from 'inversify';
import type {
  IPresentationCreatorImagesRegistry,
  IPresentationCreatorThreeDRegistry,
  IViewPortController,
  IViewPortManagerBuilder,
  IViewPortManagerStrategy
} from '@/engine/app/interfaces';
import { ViewPortKind } from '@/engine/app/interfaces';
import type {
  IImageViewPortDetails,
  IThreeDViewPortDetails,
  ViewPortInfo
} from '@/engine/app/models/viewEntity/view.entity.ts';
import type { ImagesService } from '@/engine/app/modules/images/Images.service';
import { TYPES as ImagesTYPES } from '@/engine/app/modules/images/iocTypes';
import { ViewPortImagesStrategy } from '@/engine/app/modules/images/ViewPortImagesStrategy';
import type { ViewsService } from '@/engine/app/Views.service';
import { TYPES } from '@/engine/ioc';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import { ViewPort3dStrategy } from './ViewPort3dStrategy';
import type { ViewController } from '@/engine/app/View.controller';
import { CORE_TYPES } from '@/engine/app/modules/core';
import type { ObservabilityLoggerService } from '@/app.obresvability/loggerService/observabilityLogger.service.ts';
import type { ICommandProcessor } from '@/engine/app/modules/core/interfaces/Command';
import { TYPES as INDTYPES } from '@/engine/app/modules/indications/iocTypes';
import type { IRemoveCommandsFactory } from '@/engine/app/modules/indications/IndicationRemoveHighlighter';
import type { IImageScrollStrategiesFactory } from '@/engine/app/modules/images/ImageScrollStrategies.factory.ts';
import { ThreeDPresentationFactory } from '@/engine/app/ThreeDPresentationFactory';
import { ImagesPresentationFactory } from '@/engine/app/ImagesPresentationFactory';

type IPresentationCreatorRegistry = IPresentationCreatorImagesRegistry & IPresentationCreatorThreeDRegistry;

@injectable()
export class ViewPortManagerBuilder implements IViewPortManagerBuilder {
  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _imagesService: ImagesService;

  private readonly _presentationCreatorsRegistry: IPresentationCreatorRegistry;

  private readonly _loggerService: ObservabilityLoggerService;

  private readonly _commandProcessor: ICommandProcessor;

  private readonly _removeIndicationCommandsFactory: IRemoveCommandsFactory;

  private readonly _imageScrollStrategiesFactory: IImageScrollStrategiesFactory;

  public constructor(
    @inject(TYPES.PlanningAlgorithm)
    planningAlgorithm: PlanningAlgorithm,
    @inject(ImagesTYPES.ImagesService) imagesService: ImagesService,
    @inject(TYPES.PresentationCreatorsRegistry) presentationCreatorsRegistry: IPresentationCreatorRegistry,
    @inject(CORE_TYPES.ObservabilityLoggerService) loggerService: ObservabilityLoggerService,
    @inject(CORE_TYPES.CommandProcessor) commandProcessor: ICommandProcessor,
    @inject(INDTYPES.RemoveIndicationCommandsFactory) removeIndicationCommandsFactory: IRemoveCommandsFactory,
    @inject(ImagesTYPES.ImageScrollStrategiesFactory) imageScrollStrategiesFactory: IImageScrollStrategiesFactory
  ) {
    this._planningAlgorithm = planningAlgorithm;
    this._imagesService = imagesService;
    this._presentationCreatorsRegistry = presentationCreatorsRegistry;
    this._loggerService = loggerService;
    this._commandProcessor = commandProcessor;
    this._removeIndicationCommandsFactory = removeIndicationCommandsFactory;
    this._imageScrollStrategiesFactory = imageScrollStrategiesFactory;
  }

  public buildStrategy(
    viewController: ViewController,
    viewsService: ViewsService,
    viewPort: ViewPortInfo,
    viewPortController: IViewPortController
  ): IViewPortManagerStrategy {
    switch (viewPort.kind) {
      case ViewPortKind.ImageSlice: {
        const mainSliceDescriptor = (viewPort.details as IImageViewPortDetails).sliceDescriptor;
        const presentationFactory = new ImagesPresentationFactory(
          this._presentationCreatorsRegistry,
          this._planningAlgorithm,
          viewsService,
          viewController,
          this._loggerService,
          this._commandProcessor,
          this._removeIndicationCommandsFactory,
          viewPortController,
          this._imagesService,
          viewPort.descriptor,
          mainSliceDescriptor
        );
        return new ViewPortImagesStrategy(
          viewPortController,
          this._planningAlgorithm,
          this._imagesService,
          viewsService,
          viewController,
          mainSliceDescriptor,
          presentationFactory,
          this._loggerService,
          this._commandProcessor,
          this._imageScrollStrategiesFactory
        );
      }
      case ViewPortKind.ThreeD: {
        const presentationFactory = new ThreeDPresentationFactory(
          this._presentationCreatorsRegistry,
          this._planningAlgorithm,
          viewsService,
          viewController,
          this._loggerService,
          this._commandProcessor,
          this._removeIndicationCommandsFactory,
          viewPortController,
          this._imagesService,
          viewPort.descriptor
        );
        return new ViewPort3dStrategy(
          viewPortController,
          this._planningAlgorithm,
          this._imagesService,
          viewsService,
          viewController,
          viewPort.details as IThreeDViewPortDetails,
          presentationFactory
        );
      }
      default: {
        throw new Error('Inappropriate ViewPortDescriptor');
      }
    }
  }
}
